import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import UkiesAssets from '../Admin/UkiesAssets';

export default function UkiesFullsizeBanner({ componentSettings, setComponentSettings, onChange, selectedComponent, setSelectedComponent }) {
    const [showAssetSelector, setShowAssetSelector] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const apiToken = process.env.REACT_APP_UKIES_API_TOKEN;
    
    // Get current values with defaults
    const background_image = componentSettings.background_image || '';
    const background_position_X = componentSettings.background_position_X || 50;
    const background_position_Y = componentSettings.background_position_Y || 50;
    const strapline = componentSettings.strapline || '';
    const title = componentSettings.title || '';
    const button_label = componentSettings.button_label || '';
    const button_link = componentSettings.button_link || '';
    const button_color = componentSettings.button_color || 'default';

    // Parse HTML content to extract banner configuration
    const parseHTMLContent = (htmlContent) => {
        if (!htmlContent) return null;
        
        try {
            // Create a temporary DOM element to parse the HTML
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');
            
            // Extract banner container
            const bannerContainer = doc.querySelector('.ukies-fullsize-banner');
            if (!bannerContainer) return null;
            
            let config = {
                background_image: '',
                background_position_X: 50,
                background_position_Y: 50,
                strapline: '',
                title: '',
                button_label: '',
                button_link: '',
                button_color: 'default'
            };
            
            // Extract background image from style attribute
            const style = bannerContainer.getAttribute('style') || '';
            const bgImageMatch = style.match(/background-image:\s*url\(([^)]+)\)/);
            if (bgImageMatch) {
                config.background_image = bgImageMatch[1].replace(/['"]/g, '');
            }
            
            // Extract background position
            const bgPositionMatch = style.match(/background-position:\s*(\d+)%\s+(\d+)%/);
            if (bgPositionMatch) {
                config.background_position_X = parseInt(bgPositionMatch[1]);
                config.background_position_Y = parseInt(bgPositionMatch[2]);
            }
            
            // Extract strapline (preheader)
            const straplineEl = doc.querySelector('.preheader-text');
            if (straplineEl) {
                config.strapline = straplineEl.textContent.trim();
            }
            
            // Extract title (header)
            const titleEl = doc.querySelector('.header-text');
            if (titleEl) {
                config.title = titleEl.textContent.trim();
            }
            
            // Extract button
            const buttonEl = doc.querySelector('.component-button button');
            if (buttonEl) {
                config.button_label = buttonEl.textContent.trim();
                config.button_color = buttonEl.classList.contains('highlight') ? 'highlight' : 'default';
            }
            
            const linkEl = doc.querySelector('.component-button a');
            if (linkEl) {
                config.button_link = linkEl.getAttribute('href') || '';
            }
            
            return config;
        } catch (error) {
            console.error('Error parsing HTML content:', error);
            return null;
        }
    };

    // HTML escaping function to prevent XSS
    const escapeHtml = (text) => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    };

    // Generate HTML content from current form values
    const generateHTMLContent = () => {
        const bgPosition = `${background_position_X}% ${background_position_Y}%`;
        const buttonClass = button_color === 'highlight' ? ' class="highlight"' : '';
        
        // Escape user input to prevent XSS
        const escapedStrapline = escapeHtml(strapline);
        const escapedTitle = escapeHtml(title);
        const escapedButtonLabel = escapeHtml(button_label);
        const escapedButtonLink = escapeHtml(button_link);

        // Generate button HTML only if button_label is not empty
        const buttonHtml = button_label ? `            <div class='component component-button'>
                <a href='${escapedButtonLink}'>
                    <button${buttonClass} type='button'>${escapedButtonLabel}</button>
                </a>
            </div>` : '';

        return `<div class='structure-fullwidth ukies-fullsize-banner' style='background-image: url(${background_image}); background-position: ${bgPosition}'>
    <div class='structure header-banner structure-1 lg:structure-2 items-end'>
        <div class='component component-text white text-shadow'>
            <div class='preheader-text white'>${escapedStrapline}</div>
            <div class='header-text'>${escapedTitle}</div>
${buttonHtml}
        </div>
    </div>
</div>`;
    };

    // Initialize form values from HTML content on component mount
    useEffect(() => {
        if (selectedComponent?.content && !isInitialized) {
            const parsed = parseHTMLContent(selectedComponent.content);
            if (parsed) {
                // Update the component settings with parsed values
                setComponentSettings(prev => ({
                    ...prev,
                    background_image: parsed.background_image,
                    background_position_X: parsed.background_position_X,
                    background_position_Y: parsed.background_position_Y,
                    strapline: parsed.strapline,
                    title: parsed.title,
                    button_label: parsed.button_label,
                    button_link: parsed.button_link,
                    button_color: parsed.button_color
                }));
            }
            setIsInitialized(true);
        }
    }, [selectedComponent?.content, isInitialized, setComponentSettings]);

    // Reset initialization when component changes
    useEffect(() => {
        setIsInitialized(false);
    }, [selectedComponent?.id]);

    // Handle field changes and update HTML content
    const handleFieldChange = (fieldKey, value) => {
        onChange(fieldKey, value);
        
        // Update HTML content after a brief delay to ensure state is updated
        setTimeout(() => {
            const updatedSettings = { ...componentSettings, [fieldKey]: value };
            const newContent = generateHTMLContentFromSettings(updatedSettings);
            
            // Update local component settings
            setComponentSettings(prev => ({
                ...prev,
                [fieldKey]: value,
                content: newContent
            }));
            
            // Update content in parent component
            onChange('content', newContent);
        }, 0);
    };

    // Generate HTML content from provided settings object
    const generateHTMLContentFromSettings = (settings) => {
        const bgPosition = `${settings.background_position_X || 50}% ${settings.background_position_Y || 50}%`;
        const buttonClass = settings.button_color === 'highlight' ? ' class="highlight"' : '';
        
        // Escape user input to prevent XSS
        const escapedStrapline = escapeHtml(settings.strapline || '');
        const escapedTitle = escapeHtml(settings.title || '');
        const escapedButtonLabel = escapeHtml(settings.button_label || '');
        const escapedButtonLink = escapeHtml(settings.button_link || '');

        // Generate button HTML only if button_label is not empty
        const buttonHtml = settings.button_label ? `            <div class='component component-button'>
                <a href='${escapedButtonLink}'>
                    <button${buttonClass} type='button'>${escapedButtonLabel}</button>
                </a>
            </div>` : '';

        return `<div class='structure-fullwidth ukies-fullsize-banner' style='background-image: url(${settings.background_image || ''}); background-position: ${bgPosition}'>
    <div class='structure header-banner structure-1 lg:structure-2 items-end'>
        <div class='component component-text white text-shadow'>
            <div class='preheader-text white'>${escapedStrapline}</div>
            <div class='header-text'>${escapedTitle}</div>
${buttonHtml}
        </div>
    </div>
</div>`;
    };

    // Handle asset selection for background image
    const handleAssetSelect = (asset) => {
        const imageUrl = asset.image_variations?.large 
            ? asset.image_variations.large.replace('/large', '/w=3840')
            : asset.url;
        
        handleFieldChange('background_image', imageUrl);
        setShowAssetSelector(false);
    };
    
    return (
        <div>
            <style jsx>{`
                /* Add button styling with ukies green colors */
                .add-button-green {
                    background-color: var(--ukies-light-green) !important;
                    border-color: var(--ukies-light-green) !important;
                    color: white !important;
                }
                .add-button-green:hover:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                }
                .add-button-green:focus:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                    box-shadow: 0 0 0 0.2rem rgba(59, 79, 70, 0.25) !important;
                }
                .add-button-green:active:not(:disabled) {
                    background-color: var(--ukies-dark-green) !important;
                    border-color: var(--ukies-dark-green) !important;
                    color: white !important;
                }
            `}</style>
            
            {/* Banner Configuration Header */}
            <h6 className="mb-3 mt-3">
                Banner
            </h6>
            
            {/* Background Image */}
            <Form.Group className="mb-3">
                <Form.Label>Achtergrond</Form.Label>
                <Form.Label style={{marginLeft:'1.5rem'}}>Positie</Form.Label>
                {/* Line 1: Image selection button + Position controls */}
                <div className="d-flex align-items-center gap-2 mb-2">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowAssetSelector(true)}
                        className="flex-shrink-0"
                        style={{ 
                            filter: 'grayscale(100%)',
                            minWidth: '35.2px',
                            height: '35.2px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '3.2rem'
                        }}
                    >
                        🖼️
                    </Button>
                    <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left-right" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5m14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5"/>
                            </svg>
                            <Form.Control
                                type="number"
                                value={background_position_X}
                                onChange={(e) => handleFieldChange('background_position_X', parseInt(e.target.value))}
                                min="0"
                                max="100"
                                step="1"
                                placeholder="50"
                                style={{width: '60px'}}
                                disabled
                            />
                        </div>
                        <div className="d-flex align-items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-up-down" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5m-7-14a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 1 1 .708-.708L4 13.293V1.5a.5.5 0 0 1 .5-.5"/>
                            </svg>
                            <Form.Control
                                type="number"
                                value={background_position_Y}
                                onChange={(e) => handleFieldChange('background_position_Y', parseInt(e.target.value))}
                                min="0"
                                max="100"
                                step="1"
                                placeholder="50"
                                style={{width: '60px'}}
                            />
                        </div>
                    </div>
                </div>
                {/* Line 2: Image URL */}
                <Form.Control
                    type="text"
                    value={background_image}
                    onChange={(e) => handleFieldChange('background_image', e.target.value)}
                    placeholder="Enter image URL"
                    className="mb-2 small-font-input"
                    disabled
                />
                {/* Line 3: Image preview */}
                {background_image && (
                    <div style={{overflow:'hidden',width:'100%',aspectRatio:'3/1',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <img 
                            src={background_image} 
                            alt="Background image preview" 
                            style={{ 
                                width: '100%', 
                                transform: `translate(${50 - background_position_X}%, ${50 - background_position_Y}%)`,
                                cursor: 'pointer',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                            onClick={() => window.open(background_image, '_blank')}
                        />
                    </div>
                )}
            </Form.Group>
            
            {/* Strapline */}
            <Form.Group className="mb-3">
                <Form.Label>Strapline</Form.Label>
                <Form.Control
                    type="text"
                    value={strapline}
                    onChange={(e) => handleFieldChange('strapline', e.target.value)}
                    placeholder="Enter strapline"
                />
            </Form.Group>
            
            {/* Title */}
            <Form.Group className="mb-3">
                <Form.Label>Titel</Form.Label>
                <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Enter title"
                />
            </Form.Group>
            
            {/* Button Section */}
            <h6 className="mb-2 mt-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '8px' }}>
                Knop
            </h6>
            
            {/* Button Label and Color on same line */}
            <div className="row mb-3">
                <div className="col-8">
                    <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                            type="text"
                            value={button_label}
                            onChange={(e) => handleFieldChange('button_label', e.target.value)}
                            placeholder="Enter button label"
                        />
                    </Form.Group>
                </div>
                <div className="col-4">
                    <Form.Group>
                        <Form.Label>Kleur</Form.Label>
                        <Form.Select
                            value={button_color}
                            onChange={(e) => handleFieldChange('button_color', e.target.value)}
                        >
                            <option value="default">Groen</option>
                            <option value="highlight">Oranje</option>
                        </Form.Select>
                    </Form.Group>
                </div>
            </div>
            
            {/* Button Link */}
            <Form.Group className="mb-3">
                <Form.Label>Link</Form.Label>
                <Form.Control
                    type="text"
                    value={button_link}
                    onChange={(e) => handleFieldChange('button_link', e.target.value)}
                    placeholder="Enter button link"
                />
            </Form.Group>
             
            {/* Asset Selector Dialog */}
            <UkiesAssets
                open={showAssetSelector}
                setOpen={setShowAssetSelector}
                onAssetSelect={handleAssetSelect}
                token={apiToken}
            />
        </div>
    );
}